import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  LinearProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import apiReq from 'core/api';
import { getErrorMessage } from 'utils/parseApiError';
import FilePreviewDialog from '../../components/FilePreviewDialog';
import { useSnackbar, globalConfirm } from '../../components/GlobalSnackbar';
import {
  registerProcessEventCallback,
  unregisterProcessEventCallback,
} from 'events/webSocketHandlers';
import { uploadFileChunked, PROCESS_TYPE_CONTEXT_DOCUMENT_PARSING } from 'utils/chunkedFileUpload';
import formatDate from '../../utils/formatDate';

// Styled Checkbox (Optional)
const StyledCheckbox = styled(Checkbox)({
  color: 'primary.main',
  '&.Mui-checked': {
    color: 'primary.main',
  },
});

const ACCEPTED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
];
const MAX_SIZE = 2 * 1024 * 1024 * 1024; // 2 ГБ

export default function ContextDocuments({ isDocumentsSelected }) {
  const queryClient = useQueryClient();
  const currProject = useSelector((state) => state.reducer.project.id);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const showSnackbar = useSnackbar();
  const pendingProcessesRef = useRef({});

  const waitForProcess = useCallback((processId) => {
    return new Promise((resolve, reject) => {
      pendingProcessesRef.current[processId] = { resolve, reject };
    });
  }, []);

  useEffect(() => {
    const handleProcessEvent = (_eventName, data) => {
      const pending = pendingProcessesRef.current[data.process_id];
      if (!pending) return;
      if (data.status === 'success' || data.status === 'uploaded') {
        pending.resolve(data.process_id);
        delete pendingProcessesRef.current[data.process_id];
      } else if (data.status === 'error') {
        pending.reject(new Error(data.message || 'Ошибка обработки'));
        delete pendingProcessesRef.current[data.process_id];
      }
    };
    registerProcessEventCallback(handleProcessEvent);
    return () => unregisterProcessEventCallback();
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ['contextDocs', currProject],
    queryFn: () => apiReq(`projects/context-documents?project_id=${currProject}`),
    enabled: !!currProject,
    cacheTime: 2000,
  });

  const docs = useMemo(() => data || [], [data]);

  useEffect(() => {
    const lengthSelected = docs.filter((doc) => doc.is_active).length;
    isDocumentsSelected(!!lengthSelected);
  }, [docs, isDocumentsSelected]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) {
      setSelectedFile(null);
      setFileName('');
      return;
    }
    if (!ACCEPTED_TYPES.includes(file.type) && !file.name.match(/\.(pdf|docx?|txt)$/i)) {
      showSnackbar({ message: 'Можно загружать только PDF, DOC, DOCX, TXT', severity: 'error' });
      setSelectedFile(null);
      setFileName('');
      return;
    }
    if (file.size > MAX_SIZE) {
      showSnackbar({ message: 'Файл не должен превышать 2 ГБ', severity: 'error' });
      setSelectedFile(null);
      setFileName('');
      return;
    }
    setSelectedFile(file);
    setFileName(file.name);
  };

  const handleUpload = async () => {
    if (isUploading) return;
    if (!selectedFile) {
      showSnackbar({ message: 'Выберите файл для загрузки', severity: 'error' });
      return;
    }
    if (!currProject) {
      showSnackbar({ message: 'Выберите проект', severity: 'error' });
      return;
    }
    setIsUploading(true);
    setUploadProgress(0);

    try {
      const fileId = await uploadFileChunked(
        selectedFile,
        currProject,
        PROCESS_TYPE_CONTEXT_DOCUMENT_PARSING,
        (pct) => setUploadProgress(Math.min(pct, 90))
      );

      const processData = await apiReq('process/context-document-parsing', 'POST', {
        file_id: fileId,
      });
      const processId = processData?.process_id ?? processData?.processId;
      if (!processId) {
        throw new Error('Сервер не вернул process_id');
      }

      setUploadProgress(92);
      await waitForProcess(processId);

      showSnackbar({ message: 'Файл успешно загружен!', severity: 'success' });
      setSelectedFile(null);
      setFileName('');
      queryClient.invalidateQueries({ queryKey: ['contextDocs', currProject] });
    } catch (err) {
      showSnackbar({
        message: getErrorMessage(err, 'Ошибка загрузки файла. Попробуйте еще раз.'),
        severity: 'error',
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleToggle = async (doc) => {
    await apiReq(`projects/context-documents/toggle?document_id=${doc.id}`, 'post');
    queryClient.invalidateQueries({ queryKey: ['contextDocs', currProject] });
  };

  const handleCheckboxClick = (doc) => {
    handleToggle(doc);
  };

  const handleDeleteClick = (doc) => {
    globalConfirm({
      title: 'Удалить документ?',
      message: `Вы уверены, что хотите удалить документ "${doc.name}"?`,
      confirmText: 'Удалить',
      cancelText: 'Отмена',
      onConfirm: async () => {
        await apiReq(`projects/context-documents/delete?document_id=${doc.id}`, 'delete');
        queryClient.invalidateQueries({ queryKey: ['contextDocs', currProject] });
        showSnackbar({ message: 'Документ успешно удалён', severity: 'success' });
      },
    });
  };

  const handleOpenDialog = async (doc) => {
    setSelectedDoc(doc);
    setOpenDialog(true);
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedDoc(null);
  };

  return (
    <Box>
      <Box sx={{ padding: '10px 0' }}>
        <input
          type="file"
          id="context-file-upload"
          accept=".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        <label htmlFor="context-file-upload">
          <Button
            variant="contained"
            component="span"
            style={{ width: '100%' }}
            disabled={isUploading}
          >
            Выбрать файл
          </Button>
        </label>
        {fileName && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Выбранный файл: {fileName}
          </Typography>
        )}
        {selectedFile && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
            sx={{ mt: 2, width: '100%' }}
          >
            {isUploading ? <CircularProgress size={24} /> : 'Загрузить'}
          </Button>
        )}
        {isUploading && (
          <Box sx={{ mt: 2, width: '100%' }}>
            <LinearProgress
              variant="determinate"
              value={uploadProgress}
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Box>
        )}
      </Box>
      <Box
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          wordBreak: 'break-word',
          lineHeight: '18px',
          marginTop: '10px',
        }}
      >
        {isLoading ? (
          <CircularProgress />
        ) : (
          docs.map((doc) => (
            <Card key={doc.id} sx={{ display: 'flex', alignItems: 'center', padding: 1 }}>
              <FormControlLabel
                control={
                  <StyledCheckbox
                    checked={!!doc.is_active}
                    onChange={() => handleCheckboxClick(doc)}
                    disabled={false}
                  />
                }
                label=""
                sx={{ marginRight: 1 }}
              />

              <CardContent sx={{ flex: 1, padding: '8px 0' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  <span
                    style={{
                      textDecoration: 'underline',
                      color: '#6366F1',
                      cursor: 'pointer',
                    }}
                    onClick={() => handleOpenDialog(doc)}
                  >
                    {doc.name} ({doc.file?.size ?? doc.content_length})
                  </span>
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {formatDate(doc.created_at)}
                </Typography>
              </CardContent>
              <IconButton
                sx={{ marginRight: 0 }}
                edge="end"
                aria-label="delete"
                onClick={() => handleDeleteClick(doc)}
              >
                <DeleteIcon />
              </IconButton>
            </Card>
          ))
        )}
        {!isLoading && docs.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            Нет загруженных файлов
          </Typography>
        )}
      </Box>
      <FilePreviewDialog
        open={openDialog}
        onClose={handleCloseDialog}
        url={selectedDoc?.url}
        name={selectedDoc?.name}
      />
    </Box>
  );
}
